/**
 * Universe — Nifty 500 Stock Browser
 * Searchable, filterable, paginated list with live-ish prices.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

interface Stock {
  symbol: string;
  name: string;
  index: string;
  lastPrice: number;
  prevClose: number;
  change: number;
  changePct: number;
  volume: number;
  lastDate: string;
  isHeld: boolean;
  heldQty: number;
  heldAvgPrice: number;
}

interface SearchResult {
  symbol: string;
  close: number;
}

const PAGE_SIZE = 50;

const INDEX_COLORS: Record<string, string> = {
  nifty50: "#6c5ce7",
  nifty100: "#00b894",
  nifty200: "#0984e3",
  nifty500: "#fdcb6e",
  niftymidcap100: "#e17055",
};

function fmt(n: number) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function fmtVol(v: number) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return String(v);
}

export default function Universe() {
  const navigate = useNavigate();
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [filter, setFilter] = useState<"all" | "held" | "gainers" | "losers">("all");
  const [sortBy, setSortBy] = useState<"changePct" | "volume" | "symbol">("symbol");
  const [page, setPage] = useState(1);

  // Load full universe
  const loadUniverse = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ data: Stock[]; total: number }>("/universe");
      setStocks(res.data || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUniverse(); }, [loadUniverse]);

  // Reset page when filter/sort/search changes
  useEffect(() => { setPage(1); }, [filter, sortBy, search]);

  // Debounced search
  useEffect(() => {
    if (!search.trim() || search.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get<{ data: SearchResult[] }>(`/universe/search?q=${encodeURIComponent(search)}`);
        setSearchResults(res.data || []);
      } catch { /* */ }
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Filter + sort
  const displayed = (() => {
    let list = stocks;

    // Text search
    if (search.length >= 2 && searchResults.length > 0) {
      const syms = new Set(searchResults.map((r) => r.symbol));
      list = list.filter((s) => syms.has(s.symbol));
    } else if (search.length >= 2) {
      list = list.filter((s) =>
        s.symbol.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Category filter
    if (filter === "held") list = list.filter((s) => s.isHeld);
    else if (filter === "gainers") list = list.filter((s) => s.changePct > 0);
    else if (filter === "losers") list = list.filter((s) => s.changePct < 0);

    // Sort
    list = [...list].sort((a, b) => {
      if (sortBy === "changePct") return b.changePct - a.changePct;
      if (sortBy === "volume") return b.volume - a.volume;
      return a.symbol.localeCompare(b.symbol);
    });

    return list;
  })();

  // Pagination
  const totalPages = Math.ceil(displayed.length / PAGE_SIZE);
  const paginated = displayed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageStart = (page - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(page * PAGE_SIZE, displayed.length);

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Universe</h1>
          <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "4px 0 0" }}>
            {loading ? "Loading..." : `${stocks.length} stocks · NSE`}
          </p>
        </div>
        <button
          onClick={loadUniverse}
          style={{
            background: "transparent",
            border: "1px solid var(--border)",
            borderRadius: 6,
            color: "var(--text-muted)",
            padding: "6px 12px",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          Refresh
        </button>
      </div>

      {/* Search bar */}
      <div style={{ position: "relative", marginBottom: 16 }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search symbol..."
          style={{
            width: "100%",
            maxWidth: 400,
            background: "var(--bg)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "10px 14px",
            color: "var(--text)",
            fontSize: 14,
            boxSizing: "border-box",
          }}
        />
        {searching && (
          <span style={{ position: "absolute", right: 12, top: 12, fontSize: 12, color: "var(--text-muted)" }}>
            ...
          </span>
        )}
        {/* Search dropdown */}
        {searchResults.length > 0 && (
          <div style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxWidth: 400,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            marginTop: 4,
            zIndex: 100,
            boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
            maxHeight: 300,
            overflowY: "auto",
          }}>
            {searchResults.map((r) => (
              <div
                key={r.symbol}
                onClick={() => { navigate(`/charts/${r.symbol}`); setSearch(r.symbol); }}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 14px",
                  cursor: "pointer",
                  borderBottom: "1px solid var(--border)",
                  fontSize: 13,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <span style={{ fontWeight: 600 }}>{r.symbol}</span>
                <span style={{ color: "var(--text-muted)" }}>{fmt(r.close)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {(["all", "held", "gainers", "losers"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: "4px 14px",
              borderRadius: 20,
              border: "1px solid",
              borderColor: filter === f ? "var(--primary-light)" : "var(--border)",
              background: filter === f ? "rgba(108,92,231,0.15)" : "transparent",
              color: filter === f ? "var(--primary-light)" : "var(--text-muted)",
              fontSize: 12,
              fontWeight: filter === f ? 600 : 400,
              cursor: "pointer",
              textTransform: "capitalize",
            }}
          >
            {f === "all" ? `All (${stocks.length})` : f}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as any)}
          style={{
            background: "var(--bg)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            color: "var(--text-muted)",
            fontSize: 12,
            padding: "4px 8px",
          }}
        >
          <option value="symbol">Sort: Symbol</option>
          <option value="changePct">Sort: Change %</option>
          <option value="volume">Sort: Volume</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: "var(--text-muted)" }}>
          Loading {stocks.length === 0 ? "Nifty 500..." : `${stocks.length} stocks...`}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                {["#", "Symbol", "Index", "Last Price", "Change", "Chg %", "Volume", "Held"].map((h) => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "var(--text-muted)", fontWeight: 500, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>
                    {h}
                  </th>
                ))}
                <th style={{ padding: "8px 12px" }}></th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                    No stocks match your filter
                  </td>
                </tr>
              )}
              {paginated.map((s, idx) => {
                const rowNum = (page - 1) * PAGE_SIZE + idx + 1;
                const up = s.changePct >= 0;
                const color = up ? "#00b894" : "#e17055";
                return (
                  <tr
                    key={s.symbol}
                    style={{ borderBottom: "1px solid var(--border)", cursor: "pointer" }}
                    onClick={() => navigate(`/charts/${s.symbol}`)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(108,92,231,0.05)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={{ padding: "8px 12px", color: "var(--text-muted)", fontSize: 11, width: 36 }}>
                      {rowNum}
                    </td>
                    <td style={{ padding: "8px 12px", fontWeight: 600, whiteSpace: "nowrap" }}>
                      {s.symbol}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      <span style={{
                        fontSize: 10,
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: `${INDEX_COLORS[s.index] || "#6c5ce7"}22`,
                        color: INDEX_COLORS[s.index] || "#6c5ce7",
                        fontWeight: 600,
                      }}>
                        {s.index.toUpperCase().replace("NIFTY", "")}
                      </span>
                    </td>
                    <td style={{ padding: "8px 12px", fontWeight: 500 }}>
                      {fmt(s.lastPrice)}
                    </td>
                    <td style={{ padding: "8px 12px", color }}>
                      {up ? "+" : ""}{fmt(s.change)}
                    </td>
                    <td style={{ padding: "8px 12px", color, fontWeight: 500 }}>
                      {up ? "+" : ""}{s.changePct.toFixed(2)}%
                    </td>
                    <td style={{ padding: "8px 12px", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                      {fmtVol(s.volume)}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {s.isHeld ? (
                        <span style={{ color: "#00b894", fontSize: 12, fontWeight: 600 }}>
                          {s.heldQty}
                        </span>
                      ) : (
                        <span style={{ color: "var(--text-muted)", fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "right" }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/charts/${s.symbol}`); }}
                        style={{
                          background: "rgba(108,92,231,0.15)",
                          border: "none",
                          borderRadius: 4,
                          color: "var(--primary-light)",
                          padding: "3px 10px",
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Trade
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && displayed.length > 0 && (
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 0",
          borderTop: "1px solid var(--border)",
          marginTop: 8,
        }}>
          {/* Left: showing count */}
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Showing {pageStart}–{pageEnd} of {displayed.length}
          </span>

          {/* Center: page buttons */}
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {/* First */}
            <button
              disabled={page === 1}
              onClick={() => setPage(1)}
              style={pageBtnStyle(page === 1)}
            >
              First
            </button>

            {/* Prev */}
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              style={pageBtnStyle(page === 1)}
            >
              Prev
            </button>

            {/* Page numbers — show max 7 centered around current */}
            {pageNumbers(page, totalPages).map((p) => (
              <button
                key={p}
                onClick={() => typeof p === "number" ? setPage(p) : undefined}
                disabled={p === "..."}
                style={{
                  ...pageBtnStyle(false),
                  ...(p === page ? {
                    background: "#6c5ce7",
                    color: "#fff",
                    borderColor: "#6c5ce7",
                    fontWeight: 600,
                  } : {}),
                  ...(p === "..." ? { cursor: "default", border: "none", color: "var(--text-muted)" } : {}),
                  minWidth: 32,
                }}
              >
                {p}
              </button>
            ))}

            {/* Next */}
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              style={pageBtnStyle(page === totalPages)}
            >
              Next
            </button>

            {/* Last */}
            <button
              disabled={page === totalPages}
              onClick={() => setPage(totalPages)}
              style={pageBtnStyle(page === totalPages)}
            >
              Last
            </button>
          </div>

          {/* Right: total pages */}
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Page {page} / {totalPages}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pageBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: 4,
    color: disabled ? "var(--text-muted)" : "var(--text)",
    padding: "4px 10px",
    fontSize: 12,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1,
  };
}

function pageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | "...")[] = [];

  // Always show first
  pages.push(1);

  if (current > 3) pages.push("...");

  // Pages around current
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - 2) pages.push("...");

  // Always show last
  pages.push(total);

  return pages;
}
